import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
export function KritakaLogo({ size = 22, color = "currentColor", style }) {
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 400 400", "aria-hidden": "true", style: style, children: _jsx("g", { transform: "matrix(0.398593,0,0,0.454396,37.971864,327.23095)", children: _jsx("path", { d: "M266,-124C306,-125.333 329.667,-151.667 337,-203L337,-540L-20,-540L-20,-560L833,-560L833,-540L477,-540L477,-387C499.667,-435.667 543,-460 607,-460C659.667,-460 701.333,-441.167 732,-403.5C762.667,-365.833 778,-319 778,-263C778,-207 763.333,-161.667 734,-127C704.667,-92.333 665.833,-75 617.5,-75C569.167,-75 534.333,-84.667 513,-104L518,-119C523.333,-116.333 531.333,-115 542,-115C574,-115.667 597.167,-130.333 611.5,-159C625.833,-187.667 633,-222.667 633,-264C633,-305.333 625.5,-339.167 610.5,-365.5C595.5,-391.833 576.667,-404.667 554,-404C510,-404 484.333,-378 477,-326L487,-326C487,-326 490,-330 477,-326L477,0L337,0L337,-144C314.333,-94 270.667,-69 206,-69C151.333,-69 109.167,-87.333 79.5,-124C49.833,-160.667 35,-207 35,-263C35,-319 50.667,-364.833 82,-400.5C113.333,-436.167 156,-454 210,-454C248.667,-454 278.667,-444.333 300,-425L295,-410C289.667,-412.667 282.333,-414 273,-414C241.667,-413.333 218.333,-399.667 203,-373C187.667,-346.333 180,-311.167 180,-267.5C180,-223.833 187.667,-189 203,-163C218.333,-137 239.333,-124 266,-124Z", fill: color, fillRule: "nonzero" }) }) }));
}
const SYLLABLES = [
    { latin: "kri", aksh: "कृ" },
    { latin: "ta", aksh: "त" },
    { latin: "ka", aksh: "क" },
];
const FULL_LATIN = SYLLABLES.map((s) => s.latin).join(""); // "kritaka"
const SIZE_MAP = {
    sm: { logo: 28, latin: 14, aksh: 18, depthRatio: 0.55, shiroOffset: 3, shiroThick: 1.25, gap: 10 },
    md: { logo: 44, latin: 22, aksh: 28, depthRatio: 0.55, shiroOffset: 4, shiroThick: 1.5, gap: 14 },
    lg: { logo: 64, latin: 32, aksh: 42, depthRatio: 0.55, shiroOffset: 6, shiroThick: 2, gap: 20 },
};
const SERIF_STACK = '"Source Serif 4", "Source Serif Pro", "Iowan Old Style", "Charter", "Georgia", serif';
const DEVA_STACK = '"Noto Sans Devanagari", "Mangal", "Kohinoor Devanagari", "Nirmala UI", serif';
/**
 * Measure widths of the full Latin word and each per-syllable prefix using
 * a canvas, so we can derive cube widths and the in-cell offset for the
 * shared full-word span on each front face.
 */
function useLatinMetrics(fontSize) {
    return useMemo(() => {
        if (typeof document === "undefined") {
            return {
                fullW: fontSize * 4,
                slices: [
                    { w: fontSize * 1.6, x: 0 },
                    { w: fontSize, x: fontSize * 1.6 },
                    { w: fontSize, x: fontSize * 2.6 },
                ],
            };
        }
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return {
                fullW: fontSize * 4,
                slices: [
                    { w: fontSize * 1.6, x: 0 },
                    { w: fontSize, x: fontSize * 1.6 },
                    { w: fontSize, x: fontSize * 2.6 },
                ],
            };
        }
        ctx.font = `600 ${fontSize}px ${SERIF_STACK}`;
        const widthOf = (s) => ctx.measureText(s).width;
        const fullW = widthOf(FULL_LATIN);
        // Cumulative-substring measurement gives per-slice widths that respect
        // any kerning the font applies between syllable boundaries.
        let px = 0;
        const slices = SYLLABLES.map((s) => {
            const upto = FULL_LATIN.slice(0, FULL_LATIN.indexOf(s.latin) + s.latin.length);
            const wTo = widthOf(upto);
            const slice = { x: px, w: wTo - px };
            px = wTo;
            return slice;
        });
        return { fullW, slices };
    }, [fontSize]);
}
/**
 * One cube — owns its own rotation counter so each toggle adds 180° in the
 * same rotational direction (clockwise viewed from above, i.e. negative Y
 * rotation under the right-hand rule).
 */
function KwCube({ width, height, depth, x, latinFontSize, aksh, latinOffsetX, fullLatinSpan, devaFontSize, interactive, }) {
    const [turns, setTurns] = useState(0);
    const angle = -180 * turns;
    const onEnter = () => {
        if (interactive)
            setTurns((n) => n + 1);
    };
    const onLeave = () => {
        if (interactive)
            setTurns((n) => n + 1);
    };
    return (_jsxs("div", { onMouseEnter: onEnter, onMouseLeave: onLeave, style: {
            position: "absolute",
            left: x,
            top: 0,
            width,
            height,
            transformStyle: "preserve-3d",
            transform: `rotateY(${angle}deg)`,
            transition: "transform 350ms cubic-bezier(.2,.7,.3,1)",
        }, children: [_jsx("div", { style: {
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                    backfaceVisibility: "hidden",
                    transform: `translateZ(${depth}px)`,
                }, children: _jsx("div", { style: {
                        position: "absolute",
                        left: -latinOffsetX,
                        bottom: 0,
                        whiteSpace: "pre",
                        fontFamily: SERIF_STACK,
                        fontWeight: 600,
                        fontSize: latinFontSize,
                        letterSpacing: "0.005em",
                        color: "var(--ink)",
                        lineHeight: 1,
                    }, children: fullLatinSpan }) }), _jsx("div", { style: {
                    position: "absolute",
                    inset: 0,
                    overflow: "visible",
                    backfaceVisibility: "hidden",
                    transform: `rotateY(180deg) translateZ(${depth}px)`,
                }, children: _jsx("span", { style: {
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        fontFamily: DEVA_STACK,
                        fontSize: devaFontSize,
                        fontWeight: 500,
                        color: "var(--ink)",
                        lineHeight: 1,
                        letterSpacing: 0,
                    }, children: aksh }) })] }));
}
export function KritakaWordmark({ size = "sm", interactive = true, style }) {
    const sz = SIZE_MAP[size];
    const metrics = useLatinMetrics(sz.latin);
    const cellWidths = metrics.slices.map((s) => s.w);
    const offsets = metrics.slices.map((s) => s.x);
    const lockupWidth = metrics.fullW;
    const lockupHeight = sz.aksh + sz.latin * 0.05;
    const depth = Math.round(sz.aksh * sz.depthRatio);
    const totalHeight = lockupHeight;
    return (_jsxs("div", { style: {
            display: "inline-flex",
            alignItems: "center",
            gap: sz.gap,
            fontFamily: SERIF_STACK,
            color: "var(--ink)",
            cursor: "default",
            userSelect: "none",
            ...style,
        }, children: [_jsx("span", { style: {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: totalHeight,
                    width: totalHeight,
                    flex: "0 0 auto",
                }, children: _jsx(KritakaLogo, { size: Math.round(totalHeight * 0.95) }) }), _jsx("div", { style: {
                    position: "relative",
                    width: lockupWidth,
                    height: totalHeight,
                    perspective: `${lockupWidth * 5}px`,
                    perspectiveOrigin: "50% 50%",
                }, children: SYLLABLES.map((s, i) => (_jsx(KwCube, { width: cellWidths[i], height: totalHeight, depth: depth, x: offsets[i], latinFontSize: sz.latin, aksh: s.aksh, latinOffsetX: offsets[i], fullLatinSpan: FULL_LATIN, devaFontSize: sz.aksh, interactive: interactive }, s.latin))) })] }));
}
//# sourceMappingURL=KritakaWordmark.js.map